import { useEffect, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { keycloakUtils } from "itsf-ui-common";
import { ROUTES, EServiceGroup } from "@constants";
import { useGetCart } from "@hooks/useGetCart";
import { useOffers } from "@hooks/useOffers";
import { useAddOffer } from "@hooks/useAddOffer";
import { selectOfferInCartByServiceGroup } from "@utils/offerUtils";
import { ICart, ICatalogOffer } from "@features/cart/ICart";
import { getProspectReference } from "@utils/storage";
import { useScroller } from "@hooks/useScroller";

export const useBroadbandLogin = () => {
    const { push } = useHistory();
    const { cart, cartState, setCart, getCart } = useGetCart();
    const { offerList, offerListState, getOfferList } = useOffers();
    const { addMobileOffer, addMobileOfferState } = useAddOffer();
    const { scrollToTop } = useScroller();

    const hasMobileOfferInCart = useMemo(() => Boolean(selectOfferInCartByServiceGroup(cart, EServiceGroup.MOBILE)), [
        cart,
    ]);
    const hasBroadbandOfferInCart = useMemo(() => Boolean(selectOfferInCartByServiceGroup(cart, EServiceGroup.SA_BB)), [
        cart,
    ]);

    const hasBothOffersInCart = useMemo(() => hasMobileOfferInCart && hasBroadbandOfferInCart, [
        hasMobileOfferInCart,
        hasBroadbandOfferInCart,
    ]);

    const firstMobileOffer = useMemo(
        () => offerList.find((offer) => !offer.broadbandTechnology) as ICatalogOffer | undefined,
        [offerList]
    );

    useEffect(() => {
        scrollToTop();
    }, [scrollToTop]);

    useEffect(() => {
        if (cartState.isLoading) {
            return;
        }

        const isAuthenticated = keycloakUtils.isAuthenticated();
        if (isAuthenticated || hasBothOffersInCart) {
            push(ROUTES.CART_PAGE_URL);
        }
    }, [cartState.isLoading, hasBothOffersInCart, push]);

    const handleAddMobileAndContinue = useCallback(() => {
        if (hasMobileOfferInCart) {
            push(ROUTES.CART_PAGE_URL);

            return;
        }

        if (!firstMobileOffer) {
            return;
        }

        const prospectReference = getProspectReference();
        if (prospectReference) {
            addMobileOffer(firstMobileOffer.catalogOfferCode, prospectReference).then((newCart?: ICart) => {
                if (newCart) {
                    setCart(newCart);
                    push(ROUTES.CART_PAGE_URL);
                }
            });
        }
    }, [hasMobileOfferInCart, firstMobileOffer, addMobileOffer, setCart, push]);

    return {
        hasBothOffersInCart,
        firstMobileOffer,
        handleAddMobileAndContinue,
        isPageLoading: cartState.isLoading || offerListState.isLoading,
        isAddMobileOfferLoading: addMobileOfferState.isLoading,
        error: cartState.error ?? offerListState.error ?? addMobileOfferState.error,
        retry: () => {
            getCart();
            getOfferList();
        },
    };
};
